import React from "react";

import styled from "styled-components";

import { DataGridFormControlBase } from "../DataGridFormControlBase";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { DataGrid } from "@mui/x-data-grid";

import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstantClasses, UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XDataGridEditUI extends React.Component<{ editMe: DataGridFormControl }> {
    render() {
        const gridOptions: string[] = ["Display", "Edit", "Select", "Select & Edit"];
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    label={"DataGrid Mode"}
                    propertyName={"dataGridMode"}
                    options={gridOptions}
                />

                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={() =>
                        this.props.editMe.dataGridMode === "Select" ||
                        this.props.editMe.dataGridMode === "Select & Edit"
                    }
                    observeProperties={["dataGridMode"]}
                >
                    <PropertyEditorText
                        editObject={this.props.editMe}
                        label={"Selected Value Name"}
                        propertyName={"selectedValueName"}
                    />
                </PropertyEditorCollapse>

                {this.props.editMe.renderDataGridColumnControlEditor()}
            </UISpacedBoxContent>
        );
    }
}

export class DataGridFormControl extends DataGridFormControlBase {
    dataGridMode: string = "Display";

    readonly __type: string = "DataGrid";

    render() {
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                <div data-testid={this.valueName}>
                    {this.columnValues.length > 0 && (
                        <DataGridContainer>
                            <DataGrid
                                style={UIStyleConstantClasses.gridStyling}
                                columns={this.getColumnSetting()}
                                rows={this.getRows()}
                                hideFooter
                                disableDensitySelector={true}
                                onCellEditStop={this.changeData}
                                onRowClick={this.handleData}
                            />
                        </DataGridContainer>
                    )}
                </div>
            </>
        );
    }

    getColumnSetting = () => {
        for (const columnValue of this.columnValues) {
            if (this.dataGridMode === "Edit" || this.dataGridMode === "Select & Edit") {
                columnValue.editable = true;
            }
            columnValue.renderHeader = (params) => {
                return (
                    <StyledSpan
                        fontColor={columnValue.fontColor}
                        fontSize={columnValue.fontSize}
                    >
                        {params.colDef.headerName}
                    </StyledSpan>
                );
            };
            columnValue.renderCell = (params) => {
                return (
                    <StyledSpan
                        fontColor={columnValue.fontColor}
                        fontSize={columnValue.fontSize}
                    >
                        {params.formattedValue}
                    </StyledSpan>
                );
            };
        }
        return [
            {
                field: "id",
                hide: true,
                filterable: false,
                sortable: false,
            },
            ...this.columnValues,
        ];
    };

    changeData = (params: any) => {
        const { value, field, id } = params;

        if ((this.dataGridMode === "Edit" || this.dataGridMode === "Select & Edit") && this.valueName) {
            const baseData = this.getFormDataValue(this.valueName);
            baseData[id][field] = value;
            this.setFormDataValue(this.valueName, baseData);
        }

        if (this.dataGridMode === "Select & Edit" && this.selectedValueName) {
            const row = this.getFormDataValue(this.selectedValueName);
            row[field] = value;
            this.setFormDataValue(this.selectedValueName, row);
        }
    };

    handleData = (params: any) => {
        if ((this.dataGridMode === "Select" || this.dataGridMode === "Select & Edit") && this.selectedValueName) {
            const row = { ...params.row };
            delete row["id"];
            this.setFormDataValue(this.selectedValueName, row);
        }
    };

    getRows = () => {
        if (!this.valueName) return [];
        const formDataValue = this.getFormDataValue(this.valueName);
        if (formDataValue) {
            const gridRows: object[] = [];
            let id = 0;
            for (const rowItem of formDataValue) {
                gridRows.push({
                    id,
                    ...rowItem,
                });
                id++;
            }
            return gridRows;
        } else {
            return [];
        }
    };

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.selectedValueName) return;

        let def = new ObjectDataDefinition();
        def.name = this.selectedValueName;

        return def;
    }

    renderEditUI(): JSX.Element | null {
        return <XDataGridEditUI editMe={this} />;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "dataGridMode", "__type");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "dataGridMode", "__type");
    }
}

const DataGridContainer = styled.div`
    width: 100%;
    min-height: 400px;
    height: 400px;
`;

const StyledSpan = styled.span<{ fontSize?: number; fontColor?: string }>`
    color: ${(prop) => (prop.fontColor ? prop.fontColor : UIStyleConstants.GRAY_COLOR)};
    font-size: ${(prop) => (prop.fontSize ? prop.fontSize + "px" : "0.875rem")};
`;
